import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { setCourses } from '../store/coursesSlice';
import { enrollInCourse } from '../store/progressSlice';
import { courseRegistry } from '../data/courses/courseRegistry';
import { Course } from '../types';

const CourseCatalog: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const courses = useAppSelector(state => state.courses);
  const enrolledCourses = useAppSelector(state => state.progress.enrolledCourses);

  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [difficultyFilter, setDifficultyFilter] = useState('all');

  useEffect(() => {
    dispatch(setCourses(courseRegistry));
  }, [dispatch]);

  const filteredCourses = Object.values(courses).filter((course: Course) => {
    const matchesSearch =
      course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesCategory = categoryFilter === 'all' || course.category === categoryFilter;
    const matchesDifficulty = difficultyFilter === 'all' || course.difficulty === difficultyFilter;

    return matchesSearch && matchesCategory && matchesDifficulty;
  });

  const categories = Array.from(
    new Set(Object.values(courses).map((course: Course) => course.category))
  );

  const handleEnrollClick = (courseId: string) => {
    dispatch(enrollInCourse(courseId));
    navigate(`/courses/${courseId}`);
  };

  const handleContinueClick = (courseId: string) => {
    navigate(`/courses/${courseId}`);
  };

  return (
    <Container maxWidth='lg' sx={{ py: 4 }}>
      <Typography variant='h3' component='h1' gutterBottom align='center'>
        Course Catalog
      </Typography>

      <Typography variant='subtitle1' align='center' color='text.secondary' paragraph>
        Discover interactive courses designed to help you master new skills
      </Typography>

      <Box sx={{ mb: 4 }}>
        <Grid container spacing={2} alignItems='center'>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label='Search courses'
              variant='outlined'
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryFilter}
                label='Category'
                onChange={(e: SelectChangeEvent) => setCategoryFilter(e.target.value)}
              >
                <MenuItem value='all'>All Categories</MenuItem>
                {categories.map(category => (
                  <MenuItem key={category} value={category}>
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Difficulty</InputLabel>
              <Select
                value={difficultyFilter}
                label='Difficulty'
                onChange={(e: SelectChangeEvent) => setDifficultyFilter(e.target.value)}
              >
                <MenuItem value='all'>All Levels</MenuItem>
                <MenuItem value='beginner'>Beginner</MenuItem>
                <MenuItem value='intermediate'>Intermediate</MenuItem>
                <MenuItem value='advanced'>Advanced</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      <Grid container spacing={3}>
        {filteredCourses.map((course: Course) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={course.id}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography gutterBottom variant='h5' component='h2'>
                  {course.title}
                </Typography>

                <Typography variant='body2' color='text.secondary' paragraph>
                  {course.description}
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <Chip
                    label={course.difficulty}
                    size='small'
                    color={
                      course.difficulty === 'beginner'
                        ? 'success'
                        : course.difficulty === 'intermediate'
                          ? 'warning'
                          : 'error'
                    }
                    sx={{ mr: 1 }}
                  />
                  <Chip
                    label={`${course.estimatedHours}h`}
                    size='small'
                    variant='outlined'
                    sx={{ mr: 1 }}
                  />
                  <Chip label={course.category} size='small' variant='outlined' />
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {course.tags.slice(0, 3).map(tag => (
                    <Chip key={tag} label={tag} size='small' variant='outlined' />
                  ))}
                </Box>
              </CardContent>

              <CardActions>
                {enrolledCourses.includes(course.id) ? (
                  <Button
                    size='small'
                    variant='contained'
                    onClick={() => handleContinueClick(course.id)}
                  >
                    Continue
                  </Button>
                ) : (
                  <Button
                    size='small'
                    variant='outlined'
                    onClick={() => handleEnrollClick(course.id)}
                  >
                    Enroll
                  </Button>
                )}
                <Typography variant='body2' color='text.secondary' sx={{ ml: 'auto' }}>
                  {course.modules.length} modules
                </Typography>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {filteredCourses.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant='h6' color='text.secondary'>
            No courses found matching your criteria
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Try adjusting your search terms or filters
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default CourseCatalog;
